import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  SettingsIcon,
  VolumeIcon,
  VolumeXIcon,
  RotateCcwIcon,
  CalculatorIcon
} from 'lucide-react';
import DailyExercisesHero from '../../components/student/dailyExercises/DailyExercisesHero';

const kidFieldLabel =
  'mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500 sm:text-xs';
const kidInputClass =
  'h-9 min-w-[4.5rem] rounded-xl border-2 border-sky-200 bg-white px-2.5 text-xs font-semibold text-slate-800 shadow-inner transition focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200/80 sm:text-sm';

const Soroban = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  
  // Canvas ref
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  
  // إعدادات السوروبان
  const [numberOfRods, setNumberOfRods] = useState(13);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showNumbers, setShowNumbers] = useState(true);
  const [abacus, setAbacus] = useState(null);
  const [redrawTrigger, setRedrawTrigger] = useState(0);
  
  // Constants - نمط الصورة: إطار بني، شعاع بيج، خرز بيضاوي ملون
  const DISTANCE_RODS = 60;
  const TOP_MARGIN = 30;
  const NUMBER_HEIGHT = 10;
  const LEFT_MARGIN = 10;
  const FRAME_LINE_WIDTH = 12;
  const ROD_STROKE_STYLE = '#4A4A4A';
  const ROD_LINE_WIDTH = 2;
  const DOT_STROKE_STYLE = 'rgba(0, 0, 0, 0.3)';
  const DOT_FILL_STYLE = 'rgba(0, 0, 0, 0.2)';
  const DOT_SIZE = 2;
  const BEAD_WIDTH = 52;
  const BEAD_HEIGHT = 26;
  const BEAD_STROKE = 'rgba(0,0,0,0.15)';
  const HEAVEN = BEAD_HEIGHT * 2 + FRAME_LINE_WIDTH;
  const EARTH = BEAD_HEIGHT * 5;
  const HEIGHT = HEAVEN + EARTH + FRAME_LINE_WIDTH;
  const frameColor = '#5D4037';
  const beamColor = '#E8DCC4';
  const BEAD_COLORS = ['#E53935', '#FDD835', '#43A047', '#FB8C00'];

  const getBeadColor = (rodIndex) => {
    const group = Math.floor((numberOfRods - 1 - rodIndex) / 3) % BEAD_COLORS.length;
    return BEAD_COLORS[group];
  };

  // Classes
  class AbacusClass {
    constructor(numberOfRods, mode, frameColor, showNumbers) {
      this.rods = [];
      for (let i = 0; i < numberOfRods; i++) {
        const beads = [];
        const rod = new RodClass(i + 1, beads, 0, false);
        for (let j = 0; j < 5; j++) {
          let bead;
          if (j === 0) { 
            bead = new BeadClass(rod, true, j, false);
          } else {
            bead = new BeadClass(rod, false, j, false);
          }
          beads.push(bead);
        }
        this.rods.push(rod);
      }
      this.numberOfRods = numberOfRods;
      this.mode = mode;
      this.frameColor = frameColor;
      this.showNumbers = showNumbers;
      this.middleRod = Math.floor(numberOfRods / 2) + 1;
      this.width = DISTANCE_RODS * (numberOfRods + 1);
    }

    drawFrame(context, topFrame) {
      context.save();
      const r = 8;
      const x = LEFT_MARGIN;
      const y = topFrame;
      const w = this.width;
      const h = HEIGHT;
      context.strokeStyle = frameColor;
      context.lineWidth = FRAME_LINE_WIDTH;
      context.shadowColor = 'rgba(0,0,0,0.4)';
      context.shadowOffsetX = 2;
      context.shadowOffsetY = 2;
      context.shadowBlur = 6;
      context.beginPath();
      context.moveTo(x + r, y);
      context.lineTo(x + w - r, y);
      context.quadraticCurveTo(x + w, y, x + w, y + r);
      context.lineTo(x + w, y + h - r);
      context.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      context.lineTo(x + r, y + h);
      context.quadraticCurveTo(x, y + h, x, y + h - r);
      context.lineTo(x, y + r);
      context.quadraticCurveTo(x, y, x + r, y);
      context.fillStyle = '#D2B48C';
      context.fill();
      context.strokeStyle = frameColor;
      context.lineWidth = FRAME_LINE_WIDTH;
      context.stroke();
      context.shadowColor = 'transparent';
      context.shadowBlur = 0;
      context.strokeStyle = beamColor;
      context.fillStyle = beamColor;
      context.lineWidth = 4;
      const beamY = topFrame + HEAVEN;
      context.beginPath();
      context.moveTo(x + FRAME_LINE_WIDTH / 2, beamY);
      context.lineTo(x + w - FRAME_LINE_WIDTH / 2, beamY);
      context.stroke();
      for (let i = 0, cx = LEFT_MARGIN + DISTANCE_RODS; i < this.numberOfRods; ++i, cx += DISTANCE_RODS) {
        context.fillStyle = beamColor;
        context.fillRect(cx - 6, beamY - 2, 12, 4);
      }
      const middle = Math.floor(this.numberOfRods / 2);
      context.lineWidth = 1;
      context.strokeStyle = DOT_STROKE_STYLE;
      context.fillStyle = DOT_FILL_STYLE;
      for (let i = 0, cx = LEFT_MARGIN + DISTANCE_RODS; i < this.numberOfRods; ++i, cx += DISTANCE_RODS) {
        if ((i - middle) % 3 === 0) {
          context.beginPath();
          context.arc(cx, beamY, DOT_SIZE, 0, Math.PI * 2, false);
          context.fill();
          context.stroke();
        }
      }
      context.restore();
    }

    drawRods(context, topFrame, showNumbers) {
      context.save();
      context.strokeStyle = ROD_STROKE_STYLE;
      context.lineWidth = ROD_LINE_WIDTH;
      for (let i = 0; i < this.numberOfRods; i++) {
        const rod = this.rods[i];
        rod.draw(context, topFrame, showNumbers);
      }
      context.restore();
    }

    draw(context, canvas, showNumbers) {
      context.save();
      const topFrame = TOP_MARGIN + NUMBER_HEIGHT;
      canvas.height = topFrame + HEIGHT + 10;
      context.clearRect(0, 0, canvas.width, canvas.height);
      this.drawFrame(context, topFrame);
      this.drawRods(context, topFrame, showNumbers);
      context.restore();
    }

    getTotalValue() {
      let total = 0;
      for (let i = 0; i < this.numberOfRods; i++) {
        total += (this.rods[i].value || 0) * Math.pow(10, this.numberOfRods - 1 - i);
      }
      return total;
    }

    reset() {
      for (let i = 0; i < this.numberOfRods; i++) {
        const rod = this.rods[i];
        rod.reset();
      }
      this.showNumbers = true;
    }


    disableAllRods() {
      for (let i = 0; i < this.numberOfRods; i++) {
        const rod = this.rods[i];
        rod.disabled = true;
      }
    }
  }

  class RodClass {
    constructor(position, beads, value) {
      this.position = position;
      this.beads = beads;
      this.value = 0;
      this.disabled = false;
      this.invisible = false;
    }

    drawBeads(context, topFrame) {
      for (let i = 0; i < this.beads.length; i++) {
        this.beads[i].draw(context, topFrame);
      }
    }

    drawRod(context, topFrame) {
      context.save();
      context.strokeStyle = ROD_STROKE_STYLE;
      context.lineWidth = ROD_LINE_WIDTH;
      if (this.invisible) {
        context.globalAlpha = 0;
      } else if (this.disabled) {
        context.globalAlpha = 0.1;
      } else {
        context.globalAlpha = 1;
      }
      context.beginPath();
      context.moveTo(this.evalXPos(), topFrame);
      context.lineTo(this.evalXPos(), topFrame + HEIGHT);
      context.stroke();
      context.restore();
    }

    draw(context, topFrame, showNumbers) {
      this.drawRod(context, topFrame);
      this.drawBeads(context, topFrame);
    }

    evalXPos() {
      return LEFT_MARGIN + this.position * DISTANCE_RODS;
    }

    reset() {
      for (let i = 0; i < this.beads.length; i++) {
        this.beads[i].reset();
      }
      this.value = 0;
    }

    writeValue(context, topFrame) {
      if (this.invisible) {
        context.globalAlpha = 0;
      } else if (this.disabled) {
        context.globalAlpha = 0.1;
      } else {
        context.globalAlpha = 1;
      }
      context.font = "bold 40px Courier New, Courier, monospace";
      context.textAlign = "center";
      context.lineWidth = 1;
      context.shadowColor = 'rgba(0,0,0,0.2)';
      context.shadowOffsetX = 3;
      context.shadowOffsetY = 3;
      context.shadowBlur = 8;
      context.fillStyle = '#228B22'; // Forest green
      context.strokeStyle = '#228B22'; // Forest green
      context.fillText(this.value, this.evalXPos(), TOP_MARGIN);
      context.strokeText(this.value, this.evalXPos(), TOP_MARGIN);
    }
  }

  class BeadClass {
    constructor(rod, heaven, order, active) {
      this.rod = rod;
      this.heaven = heaven;
      this.order = order;
      this.active = active;
    }

    getCenter(topFrame) {
      return this.evalPosition(topFrame);
    }

    evalPosition(topFrame) {
      const x = LEFT_MARGIN + this.rod.position * DISTANCE_RODS;
      let y;

      if (this.heaven) {
        if (this.active) {
          y = topFrame + HEAVEN - BEAD_HEIGHT / 2 - FRAME_LINE_WIDTH / 2;
        } else {
          y = topFrame + BEAD_HEIGHT / 2 + FRAME_LINE_WIDTH / 2;
        }
      } else {
        if (this.active) {
          y = topFrame + HEAVEN + (this.order - 1) * BEAD_HEIGHT + BEAD_HEIGHT / 2 + FRAME_LINE_WIDTH / 2;
        } else {
          y = topFrame + HEAVEN + this.order * BEAD_HEIGHT + BEAD_HEIGHT / 2 + FRAME_LINE_WIDTH / 2;
        }
      }

      return { x, y };
    }

    createPath(context, topFrame) {
      const center = this.getCenter(topFrame);
      context.beginPath();
      context.ellipse(center.x, center.y, BEAD_WIDTH / 2, BEAD_HEIGHT / 2, 0, 0, Math.PI * 2);
    }

    draw(context, topFrame) {
      context.save();
      const rodIndex = this.rod.position - 1;
      const baseColor = getBeadColor(rodIndex);
      const center = this.getCenter(topFrame);
      const gradient = context.createRadialGradient(
        center.x - 10, center.y - 10, 0,
        center.x, center.y, BEAD_WIDTH / 2 + 5
      );
      gradient.addColorStop(0, 'rgba(255,255,255,0.4)');
      gradient.addColorStop(0.4, baseColor);
      gradient.addColorStop(1, baseColor);
      context.fillStyle = gradient;
      context.shadowColor = 'rgba(0,0,0,0.25)';
      context.shadowOffsetX = 2;
      context.shadowOffsetY = 2;
      context.shadowBlur = 4;
      if (this.rod.invisible) {
        context.globalAlpha = 0;
      } else if (this.rod.disabled) {
        context.globalAlpha = 0.1;
      } else {
        context.globalAlpha = 1;
      }
      context.strokeStyle = BEAD_STROKE;
      context.lineWidth = 1;
      this.createPath(context, topFrame);
      context.fill();
      context.stroke();
      context.restore();
    }

    reset() {
      this.active = false;
    }
  }

  // Helper functions
  const windowToCanvas = (x, y, canvas) => {
    const bbox = canvas.getBoundingClientRect();
    return { 
      x: x - bbox.left * (canvas.width / bbox.width),
      y: y - bbox.top * (canvas.height / bbox.height)
    };
  };

  const getBead = (rod, heaven, order) => {
    for (let i = 0; i < rod.beads.length; i++) {
      if (rod.beads[i].heaven === heaven && rod.beads[i].order === order) {
        return rod.beads[i];
      }
    }
  };

  const clickedBead = (bead) => {
    if (bead.heaven) {
      if (bead.active) {
        bead.active = false;
        bead.rod.value -= 5;
      } else {
        bead.active = true;
        bead.rod.value += 5;
      }
    } else {
      if (bead.active) {
        bead.active = false;
        bead.rod.value--;
        for (let i = bead.order + 1; i <= 4; i++) {
          const nextBead = getBead(bead.rod, false, i);
          if (nextBead.active) {
            nextBead.active = false;
            nextBead.rod.value--;
          }
        }
      } else {
        bead.active = true;
        bead.rod.value++;
        for (let i = 1; i < bead.order; i++) {
          const nextBead = getBead(bead.rod, false, i);
          if (!nextBead.active) {
            nextBead.active = true;
            nextBead.rod.value++;
          }
        }
      }
    }
  };

  const playBeadSound = () => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  };

  const clickOrTouch = (e) => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const loc = windowToCanvas(e.clientX, e.clientY, canvas);
    let found = false;
    
    for (let i = 0; i < abacus.numberOfRods && !found; i++) {
      const currentRod = abacus.rods[i];
      for (let j = 0; j < currentRod.beads.length && !found; j++) {
        const currentBead = currentRod.beads[j];
        currentBead.createPath(context, TOP_MARGIN + NUMBER_HEIGHT);
        if (context.isPointInPath(loc.x, loc.y)) {
          found = true;
          playBeadSound();
          clickedBead(currentBead);
        }
      }
    }
    
    context.clearRect(0, 0, canvas.width, canvas.height);
    abacus.draw(context, canvas, showNumbers);
    setRedrawTrigger((v) => v + 1);
  };

  // Initialize abacus
  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      const newAbacus = new AbacusClass(numberOfRods, 'normal', frameColor, showNumbers);
      canvas.width = newAbacus.width + 2 * LEFT_MARGIN;
      
      setAbacus(newAbacus);
      newAbacus.draw(context, canvas, showNumbers);
    }
  }, [numberOfRods, showNumbers]);


  const resetAbacus = () => {
    if (abacus) {
      abacus.reset();
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      abacus.draw(context, canvas, showNumbers);
      setRedrawTrigger((v) => v + 1);
    }
  };

  const totalValue = abacus ? abacus.getTotalValue() : 0;

  return (
    <div className="mx-auto min-h-screen w-full max-w-6xl space-y-5 px-3 pb-10 sm:space-y-6 sm:px-4 sm:pb-12" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Audio element for bead sound */}
      <audio ref={audioRef} preload="auto">
        <source src="https://www.rightlobemath.com/digitalabacus/clac.wav" type="audio/wav" />
      </audio>

      <DailyExercisesHero
        title={t('navigation.soroban')}
        description={t('soroban.pageDescription')}
        icon={CalculatorIcon}
        iconClassName="text-amber-700"
      />

      {/* شريط الإعدادات */}
      <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/90 shadow-[0_8px_0_0_rgba(15,23,42,0.06)] backdrop-blur-sm">
        <div className="border-b border-slate-200/90 bg-gradient-to-r from-amber-50/90 via-white to-sky-50/50 px-4 py-3 sm:px-5">
          <h2 className="flex items-center gap-2 text-base font-black text-slate-900 sm:text-lg">
            <SettingsIcon className="h-5 w-5 shrink-0 text-amber-700" strokeWidth={2.25} aria-hidden />
            {t('soroban.settings')}
          </h2>
        </div>
        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-5 sm:p-6">
          <div className="flex min-w-0 flex-1 flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="min-w-0 sm:max-w-[12rem]">
              <label htmlFor="soroban-rods" className={kidFieldLabel}>
                {t('soroban.columns')}
              </label>
              <select
                id="soroban-rods"
                value={numberOfRods}
                onChange={(e) => setNumberOfRods(parseInt(e.target.value, 10))}
                className={`${kidInputClass} w-full`}
              >
                {Array.from({ length: 15 }, (_, index) => index + 1).map((num) => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            <label className="flex min-h-[2.25rem] cursor-pointer items-center gap-2.5 rounded-xl border-2 border-slate-200 bg-slate-50/80 px-3 py-2 sm:min-h-0 sm:self-end">
              <input
                type="checkbox"
                checked={showNumbers}
                onChange={(e) => setShowNumbers(e.target.checked)}
                className="h-4 w-4 rounded border-2 border-sky-300 text-sky-600 focus:ring-sky-500"
              />
              <span className="text-xs font-bold text-slate-700 sm:text-sm">{t('soroban.showNumbers')}</span>
            </label>
          </div>

          <div className="flex flex-wrap gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`inline-flex min-h-[2.5rem] items-center gap-2 rounded-xl border-2 px-4 py-2 text-xs font-bold transition sm:text-sm ${
                soundEnabled
                  ? 'border-emerald-300 bg-gradient-to-b from-emerald-50 to-white text-emerald-800 shadow-sm hover:brightness-[1.02]'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {soundEnabled ? <VolumeIcon className="h-4 w-4 shrink-0" strokeWidth={2.25} /> : <VolumeXIcon className="h-4 w-4 shrink-0" strokeWidth={2.25} />}
              {soundEnabled ? t('soroban.soundOn') : t('soroban.soundOff')}
            </button>

            <button
              type="button"
              onClick={resetAbacus}
              className="inline-flex min-h-[2.5rem] items-center gap-2 rounded-xl border-2 border-red-300 bg-gradient-to-b from-red-50 to-white px-4 py-2 text-xs font-bold text-red-800 shadow-sm transition hover:brightness-[1.02] sm:text-sm"
            >
              <RotateCcwIcon className="h-4 w-4 shrink-0" strokeWidth={2.25} />
              {t('soroban.reset')}
            </button>
          </div>
        </div>
      </section>

      {/* السوروبان Canvas */}
      <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-amber-200/60 bg-gradient-to-b from-amber-50/50 via-slate-50/80 to-sky-50/40 p-4 shadow-[0_8px_0_0_rgba(180,83,9,0.08)] sm:p-6">
        <div className="flex flex-col items-center gap-3">
          {showNumbers && (
            <div
              className="flex min-w-[140px] items-center justify-center rounded-2xl border-2 border-white/90 bg-white px-8 py-3 shadow-md"
            >
              <span
                className="text-4xl font-black tabular-nums"
                style={{
                  background: 'linear-gradient(135deg, #43A047 0%, #7CB342 50%, #FB8C00 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {totalValue}
              </span>
            </div>
          )}
          <canvas
            ref={canvasRef}
            onClick={clickOrTouch}
            onTouchEnd={(e) => {
              e.preventDefault();
              if (e.changedTouches?.[0]) {
                const touch = e.changedTouches[0];
                clickOrTouch({ clientX: touch.clientX, clientY: touch.clientY });
              }
            }}
            className="max-w-full cursor-pointer touch-none select-none"
            style={{
              height: 'auto',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none'
            }}
          />
        </div>
      </section>

      {/* تعليمات الاستخدام */}
      <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/90 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
        <div className="border-b border-slate-200/90 bg-gradient-to-r from-sky-50/90 via-white to-amber-50/50 px-4 py-3 sm:px-5">
          <h3 className="text-base font-black text-slate-900 sm:text-lg">{t('soroban.instructions')}</h3>
        </div>
        <ul className="space-y-2.5 p-4 text-sm font-semibold leading-relaxed text-slate-700 sm:p-6 sm:text-base">
          <li className="flex gap-2">
            <span className="text-amber-600" aria-hidden>•</span>
            <span>{t('soroban.instructionUpperBead')}</span>
          </li>
          <li className="flex gap-2">
            <span className="text-amber-600" aria-hidden>•</span>
            <span>{t('soroban.instructionLowerBeads')}</span>
          </li>
          <li className="flex gap-2">
            <span className="text-amber-600" aria-hidden>•</span>
            <span>{t('soroban.instructionColumns')}</span>
          </li>
        </ul>
      </section>
    </div>
  );
};

export default Soroban;
